/**
 * Hosts that get features ahead of a prod release. Merging to `main` deploys
 * dev AND prod, so a dev-only feature has to be gated at runtime; drop the
 * gate when the feature ships to prod.
 */
const PRE_RELEASE_HOSTS = ['dev.dashboard.martcart.com', 'localhost', '127.0.0.1'];

export const isPreReleaseHost = () =>
  PRE_RELEASE_HOSTS.includes(window.location.hostname);

/**
 * Features shown only on pre-release hosts, each with the release day it's
 * planned for ('YYYY-MM-DD', or 'unscheduled'). A feature is a card (its
 * data-track name), a page (<ReleaseGate feature>), a menu item
 * (`feature` on a sidebar link) or a change inside a page
 * (isReleased(name) ? new : old).
 *
 * To ship one, delete its name here. A card's gate can stay (the Engagement
 * page then judges it on prod use, TRACKED_CARDS.onProd); any other gate goes
 * in the same PR. trackedCards.test.ts fails if a name here isn't gated, or a
 * gate names something that is neither listed here nor a tracked card.
 */
export const PRE_RELEASE: Readonly<Record<string, string>> = {
  'Battery Compliance': 'unscheduled',
  'Cart Health': '2026-11-05',
  'Cart Status': '2026-11-05',
  // The carts behind each Cart Status count (api#337, on prod via api#339).
  'Cart Status drill-down': '2026-11-05',
  'Cart Usage': '2026-11-05',
  // Admin page of every signal carts send (api#343).
  'Cart Signals': '2026-11-05',
  'Fleet Overview': '2026-11-05',
  // Search box on the customer My Fleet tab.
  'My Fleet filter': '2026-11-05',
  'Pipeline Performance': '2026-11-05',
  // Main Dashboard grouped under section headings (frontend-portal#404).
  'Dashboard sections': 'unscheduled',
  // Service's week-over-week changes (api#357). Not 'This Week': that's the
  // customer portal's card.
  'Service This Week': 'unscheduled',
  // Faults before vs after each firmware update (api#355). Needs api#355 on
  // prod, and Engineering's definition of "latest version", before a day.
  'Firmware Impact': 'unscheduled',
  // New / recurring / recovered on the Cart Health card (api#350, api#354).
  'Cart Health trends': '2026-11-05',
  // Admins/Service card: carts ready when each store opens (api#350).
  // Needs api#365 on prod and the prod backfill first.
  'Ready at Opening': '2026-11-05',
  // Admins/Service card: charging after close and quiet carts (api#366).
  // Needs api#370 on prod and the prod backfill before a day.
  'Charging & Use': 'unscheduled',
};

export const isPreRelease = (name: string) =>
  Object.prototype.hasOwnProperty.call(PRE_RELEASE, name);

/** True when every named feature is out of pre-release, or on a pre-release host. */
export const isReleased = (...names: string[]) =>
  isPreReleaseHost() || names.every((name) => !isPreRelease(name));

/** Drops the items (menu links, tabs) whose `feature` isn't released here. */
export const releasedOnly = <T extends object>(items: readonly T[]): T[] =>
  items.filter((item) => {
    const { feature } = item as { feature?: unknown };
    return typeof feature !== 'string' || isReleased(feature);
  });
